import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CircleAlert } from 'lucide-react';
import { useAuth } from '@Contexts/auth';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/input-otp';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { cn } from '@Utils/cn';

const noiseStyle = {
  backgroundImage: `url("data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='noiseFilter'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23noiseFilter)' opacity='0.1'/></svg>")`,
  backgroundRepeat: 'repeat',
  backgroundSize: '220px 220px',
};

const phoneSchema = z.object({
  phoneNumber: z
    .string()
    .refine((v) => v.replace(/\D/g, '').length === 10, {
      message: 'Enter a 10-digit US phone number.',
    }),
});

const otpSchema = z.object({
  code: z
    .string()
    .length(6, 'Enter the 6-digit code.')
    .regex(/^\d+$/, 'Code must be numeric.'),
});

type PhoneFormValues = z.infer<typeof phoneSchema>;
type OtpFormValues = z.infer<typeof otpSchema>;

const normalizePhone = (raw: string) => {
  const trimmed = raw.trim();
  if (trimmed.startsWith('+')) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
};

const formatPhone = (raw: string) => {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const PhoneStep = () => {
  const { sendOtp } = useAuth();

  const form = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { phoneNumber: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async (values: PhoneFormValues) => {
    try {
      const result = await sendOtp(normalizePhone(values.phoneNumber));

      if (!result.ok) {
        setError('root', {
          type: 'server',
          message: result.message || 'Could not send code.',
        });
      }
    } catch (_err) {
      setError('root', {
        type: 'server',
        message: 'An error occurred. Please try again.',
      });
    }
  };

  return (
    <Form {...form}>
      <form className="mt-2 space-y-6" onSubmit={handleSubmit(onSubmit)}>
        {errors.root?.message && (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{errors.root.message}</AlertDescription>
          </Alert>
        )}

        <FormField
          control={control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white">Phone number</FormLabel>
              <FormControl>
                <Input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="(555) 123-4567"
                  maxLength={14}
                  className="border-zinc-300 bg-white text-zinc-900 aria-invalid:bg-red-100"
                  value={field.value}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  onChange={(e) => field.onChange(formatPhone(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          loading={isSubmitting}
          className="mx-auto block px-12"
        >
          Send code
        </Button>
      </form>
    </Form>
  );
};

const OtpStep = () => {
  const { verifyOtp, resendOtp, cancelOtp } = useAuth();
  const [resendState, setResendState] = useState<
    'idle' | 'sending' | 'sent' | 'failed'
  >('idle');

  const form = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: { code: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async (values: OtpFormValues) => {
    const result = await verifyOtp(values.code);
    if (!result.ok) {
      setError('root', {
        type: 'server',
        message: result.message || 'Invalid verification code.',
      });
    }
  };

  const handleResend = async () => {
    setResendState('sending');
    const ok = await resendOtp();
    setResendState(ok ? 'sent' : 'failed');
  };

  return (
    <Form {...form}>
      <form className="mt-2 space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="text-center text-sm text-zinc-300">
          We sent a 6-digit code to your phone. Enter it below to sign in.
        </div>

        {errors.root?.message && (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{errors.root.message}</AlertDescription>
          </Alert>
        )}

        <FormField
          control={control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white">Verification code</FormLabel>
              <FormControl>
                <InputOTP
                  maxLength={6}
                  value={field.value}
                  onChange={field.onChange}
                  containerClassName="justify-center"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormMessage className="text-center" />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          loading={isSubmitting}
          className="mx-auto block px-12"
        >
          Verify
        </Button>

        <div className="flex items-center justify-center gap-4 text-sm">
          <button
            type="button"
            className="text-zinc-300 underline-offset-2 hover:underline disabled:opacity-50"
            onClick={handleResend}
            disabled={resendState === 'sending'}
          >
            {resendState === 'sending'
              ? 'Resending…'
              : resendState === 'sent'
                ? 'Code resent'
                : resendState === 'failed'
                  ? 'Resend failed — try again'
                  : 'Resend code'}
          </button>
          <span className="text-zinc-600">·</span>
          <button
            type="button"
            className="text-zinc-300 underline-offset-2 hover:underline"
            onClick={cancelOtp}
          >
            Use a different number
          </button>
        </div>
      </form>
    </Form>
  );
};

const Login = () => {
  const { pendingPhone } = useAuth();

  return (
    <div
      className={cn(
        'dark relative flex h-full w-full items-center justify-center bg-zinc-900 p-4',
      )}
      style={{ ...noiseStyle }}
    >
      <div className="w-md max-w-full">
        <Card className="shadow-2xl">
          <CardContent>
            <img
              src="/one-point-logo-horizontal.webp"
              alt="One Point"
              className="mx-auto mt-4 mb-8 w-56 max-w-full"
            />
            {pendingPhone ? <OtpStep /> : <PhoneStep />}
          </CardContent>
        </Card>

        <div className="mx-auto mt-3 flex cursor-default items-center justify-center gap-0.5 overflow-hidden p-4 text-sm text-nowrap text-zinc-500 transition-all duration-200">
          <span>powered by</span>
          <img
            src="/aoh-logomark.png"
            alt="Art of Hospitality Solutions"
            className="inline h-5 w-auto pb-0.5"
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
